import React from 'react';
import s from './s.module.less';
import HeroBlob from '../../images/hero-blob.svg';
import PortalBlob from '../../images/portal-blob2.svg';
import ShieldGreen from '../../images/shield-green.svg';
import KlarityWordmark from '../../images/klarity-wordmark.png';
import ChevronSm from '../../images/chevron-sm.svg';
import Play from '../../images/play.svg';
import RxBadge from '../../images/rx-badge.svg';
import Star from '../../images/star.svg';
import Stars5 from '../../images/stars-5.svg';
import Stars4 from '../../images/stars-4.svg';
import VerifiedBadge from '../../images/verified-badge.svg';
import Menu from '../../images/menu.svg';

type Props = {
    // the provider whose link is being shared — the only identity shown to the patient
    name: string;
    credential?: string;
    // comma separated, mirrors the specialty line on the public Klarity profile
    specialty?: string;
    avatar?: string;
    link: string;
    activeIndex: number;
};

const DEFAULT_AVATAR =
    'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" fill="%23E5E7EB"/><circle cx="32" cy="25" r="11" fill="%239CA3AF"/><path d="M10 62c3-13 11-19 22-19s19 6 22 19z" fill="%239CA3AF"/></svg>';

// "Dr. Sarah Kim" -> "Dr. Kim", "Mary Taylor" -> "Mary"
const getShortName = (name: string) => {
    const parts = name.split(' ').filter(Boolean);
    if (parts.length === 0) {
        return name;
    }
    const isDr = /^dr\.?$/i.test(parts[0]);
    return isDr ? `${parts[0]} ${parts[parts.length - 1]}` : parts[0];
};

const getFirstName = (name: string) => {
    const parts = name.split(' ').filter(Boolean);
    if (parts.length === 0) {
        return name;
    }
    return /^dr\.?$/i.test(parts[0]) ? parts[1] || parts[0] : parts[0];
};

// the "what does this mean?" affordance the public profile puts next to a
// credential and next to the prior-authorization chip
const HelpIcon = () => (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
        <circle cx="8" cy="8" r="6.6" />
        <path d="M6.4 6.3a1.7 1.7 0 013.2.6c0 1.2-1.6 1.4-1.6 2.3" strokeLinecap="round" />
        <circle cx="8" cy="11.4" r=".65" fill="currentColor" stroke="none" />
    </svg>
);

const PatientPreview: React.FC<Props> = ({ name, credential, specialty, avatar, link, activeIndex }) => {
    const photo = avatar || DEFAULT_AVATAR;
    const shortName = getShortName(name);
    const firstName = getFirstName(name);
    const credentials = (credential || '')
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);

    return (
        <div className={s.phone}>
            <div className={s.screen}>
                {/* 1. Message arrives */}
                <div className={`${s.slide} ${activeIndex === 0 ? s.slideActive : ''}`}>
                    <div className={s.statusbar}>
                        <span>9:41</span>
                        <span>▮▮▮</span>
                    </div>
                    <div className={s.appbar}>Messages</div>
                    <div className={s.slideBody}>
                        <div className={s.smsFrom}>Klarity Health · today</div>
                        <div className={s.bubble}>
                            Hi Anna — thanks for coming in today. If you have 30 seconds, would you share how your visit
                            with {name} went?
                            <br />
                            <br />
                            <span className={s.bubbleLink}>{link}</span>
                        </div>
                        <div className={s.tapWrap}>
                            <span className={s.tap}>
                                <svg
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M5 7V3.5a1.5 1.5 0 013 0V8" />
                                    <path d="M8 7.5V6a1.4 1.4 0 012.8 0v2" />
                                    <path d="M10.8 8V7a1.4 1.4 0 012.7 0v3.6a3.4 3.4 0 01-3.4 3.4H8.6a3 3 0 01-2.4-1.2L4 10" />
                                </svg>
                                Patient taps the link
                            </span>
                        </div>
                    </div>
                </div>

                {/* 2. Patient portal sign-up */}
                <div className={`${s.slide} ${activeIndex === 1 ? s.slideActive : ''}`}>
                    <div className={s.pp}>
                        <div className={s.ppDoc}>
                            <div className={s.ppoPage}>
                                <img className={s.ppoBlob} src={PortalBlob} alt="" />
                                <div className={s.ppoProv}>
                                    <img className={s.ppoAvatar} src={photo} alt="" />
                                    <div>
                                        <div className={s.ppoName}>{name}</div>
                                        <div className={s.ppoNet}>
                                            <img className={s.ppShield} src={ShieldGreen} alt="" />
                                            <span>
                                                In-network for Aetna, Aetna Better Health, All Savers, Allegiance...
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className={s.ppoHd}>
                                    <div className={s.ppoH1}>
                                        Hello and welcome!
                                        <br />
                                        Your health matters to us.
                                    </div>
                                    <div className={s.ppoP}>
                                        Thank you for choosing our care team to support your wellbeing. Sign up for the
                                        patient portal using your phone number to easily manage your appointments and
                                        message your providers.
                                    </div>
                                </div>
                                <div className={s.ppoPb}>
                                    <span>Powered by</span>
                                    <img src={KlarityWordmark} alt="Klarity" />
                                </div>
                                <div className={s.ppoSheet} />
                                <div className={s.ppoForm}>
                                    <div className={s.ppoT}>Sign up for your patient portal</div>
                                    <div>
                                        <div className={s.ppoLabel}>Phone number</div>
                                        <div className={s.ppoInput}>
                                            <span className={s.ppoCc}>+1</span>
                                            <img src={ChevronSm} alt="" />
                                            <span className={s.ppoPh}>(555) 123-4567</span>
                                        </div>
                                        <div className={s.ppoHelp}>
                                            We&apos;ll send a verification code to this number
                                        </div>
                                    </div>
                                    <div className={s.ppoCta}>Continue</div>
                                    <div className={s.ppoAlt}>
                                        <i />
                                        <p>
                                            Already have an account? <b>Log in</b>
                                        </p>
                                        <i />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3. Rate the visit */}
                <div className={`${s.slide} ${activeIndex === 2 ? s.slideActive : ''}`}>
                    <div className={s.statusbar}>
                        <span>9:42</span>
                        <span>▮▮▮</span>
                    </div>
                    <div className={s.appbar}>Rate your visit</div>
                    <div className={s.slideBody}>
                        <div className={s.card}>
                            <div className={s.stars}>
                                {[0, 1, 2, 3, 4].map((i) => (
                                    <svg key={i} viewBox="0 0 18 17" fill="#FFCB45">
                                        <path d="M9 0l2.76 5.59 6.17.9-4.47 4.35 1.06 6.16L9 14.14 3.48 17l1.06-6.16L.07 6.49l6.17-.9L9 0z" />
                                    </svg>
                                ))}
                            </div>
                            <div className={s.tagrow}>
                                <span className={`${s.tag} ${s.tagOn}`}>Good listener</span>
                                <span className={`${s.tag} ${s.tagOn}`}>Professional</span>
                                <span className={s.tag}>Easy to reach</span>
                                <span className={s.tag}>Explained clearly</span>
                            </div>
                            <div className={s.textarea}>
                                {shortName} really listened and explained my options without rushing…
                            </div>
                            <div className={s.vis}>
                                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
                                    <path d="M1 8s2.5-4.5 7-4.5S15 8 15 8s-2.5 4.5-7 4.5S1 8 1 8z" />
                                    <circle cx="8" cy="8" r="1.8" />
                                </svg>
                                Post publicly on {shortName}&apos;s profile
                            </div>
                            <div className={s.miniBtn}>Submit review</div>
                        </div>
                    </div>
                </div>

                {/* 4. The review goes live on the public profile */}
                <div className={`${s.slide} ${activeIndex === 3 ? s.slideActive : ''}`}>
                    <div className={s.pp}>
                        <div className={s.ppDoc}>
                            <div className={s.pppScroll}>
                                <div className={s.pppPage}>
                                    <div className={s.pppHero}>
                                        <img className={s.pppBlob} src={HeroBlob} alt="" />
                                    </div>
                                    <div className={s.pppBack}>
                                        <svg
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.7"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <line x1="13" y1="8" x2="3" y2="8" />
                                            <polyline points="7 4 3 8 7 12" />
                                        </svg>
                                        <span>Back to results page</span>
                                    </div>
                                    <div className={s.pppPhoto}>
                                        <img src={photo} alt="" />
                                        <span className={s.pppPlay}>
                                            <img src={Play} alt="" />
                                            <span>Play</span>
                                        </span>
                                    </div>
                                    <div className={s.pppShare}>Share profile</div>
                                    <div className={s.pppRating}>
                                        <img src={Star} alt="" />
                                        <span>4.95</span>
                                        <span>·</span>
                                        <span className={s.pppLk}>43 reviews</span>
                                    </div>
                                    <div className={s.pppInfo}>
                                        <div>
                                            <div className={s.pppName}>
                                                <h3>{name}</h3>
                                                <img src={RxBadge} alt="" />
                                            </div>
                                            <div className={s.pppSub}>{credential || 'Klarity provider'}</div>
                                        </div>
                                        {credentials.length > 0 && (
                                            <div className={s.pppCreds}>
                                                {credentials.map((cred) => (
                                                    <span className={s.ppRow} key={cred}>
                                                        {cred}
                                                        <HelpIcon />
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {!!specialty && <div className={s.pppSpec}>{specialty}</div>}
                                        <div className={s.pppAcc}>
                                            <img src={ShieldGreen} alt="" />
                                            <span>Accepts Aetna, and All Savers.</span>
                                        </div>
                                        <div className={s.pppChips}>
                                            <span className={s.pppChip}>10 years of experience</span>
                                            <span className={s.pppChip}>
                                                Support prior authorization
                                                <HelpIcon />
                                            </span>
                                        </div>
                                        <div className={s.pppAsk}>Ask a question</div>
                                    </div>

                                    <div className={s.pppSum}>
                                        <div className={s.pppSumL}>
                                            <div className={s.pppSumV}>4.95</div>
                                            <img src={Stars5} alt="" />
                                            <div className={s.pppSumC}>43 reviews</div>
                                        </div>
                                        <div className={s.pppSumR}>
                                            <div className={s.pppVp}>
                                                <img className={s.pppVpB} src={VerifiedBadge} alt="" />
                                                <span>Verified patient</span>
                                                <img className={s.pppVpS} src={Stars5} alt="" />
                                            </div>
                                            <p>
                                                &quot;{firstName} really listened and explained my options without
                                                rushing.&quot;
                                            </p>
                                            <span className={s.pppSumLink}>Read all reviews</span>
                                        </div>
                                    </div>

                                    <div className={s.pppTabs}>
                                        <span className={s.pppTab}>About {firstName}</span>
                                        <span className={`${s.pppTab} ${s.pppTabOn}`}>Reviews</span>
                                        <span className={s.pppTab}>Specialties</span>
                                        <span className={s.pppTab}>Services</span>
                                    </div>

                                    <div className={s.pppRevs}>
                                        <div className={s.pppRevsHd}>
                                            <img src={Star} alt="" />
                                            <span className={s.pppRevsV}>4.95</span>
                                            <span className={s.pppRevsD}>·</span>
                                            <span className={s.pppRevsN}>43 reviews</span>
                                            <span className={s.pppSel}>
                                                Recent
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.8"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <polyline points="7 10 12 15 17 10" />
                                                </svg>
                                            </span>
                                        </div>

                                        <div className={`${s.pppRev} ${s.pppRevNew}`}>
                                            <span className={s.pppNew}>Just posted</span>
                                            <div className={s.pppRevT}>
                                                <span className={s.pppWho}>
                                                    <img src={VerifiedBadge} alt="" />
                                                    Verified patient
                                                </span>
                                                <span className={s.pppWhen}>A day ago</span>
                                            </div>
                                            <img className={s.pppStars} src={Stars5} alt="" />
                                            <p>
                                                &quot;{firstName} really listened and explained my options without
                                                rushing. Booking was easy too.&quot;
                                            </p>
                                            <div className={s.pppTags}>
                                                <span className={s.pppTag}>Good listener</span>
                                                <span className={s.pppTag}>Explained clearly</span>
                                            </div>
                                        </div>

                                        <div className={s.pppRev}>
                                            <div className={s.pppRevT}>
                                                <span className={s.pppWho}>
                                                    <img src={VerifiedBadge} alt="" />
                                                    Verified patient
                                                </span>
                                                <span className={s.pppWhen}>3 days ago</span>
                                            </div>
                                            <img className={s.pppStars} src={Stars5} alt="" />
                                            <p>
                                                &quot;Follow-up was quick and I never felt rushed. Genuinely glad I
                                                found this practice.&quot;
                                            </p>
                                            <div className={s.pppTags}>
                                                <span className={s.pppTag}>Professional</span>
                                                <span className={s.pppTag}>Easy to reach</span>
                                            </div>
                                        </div>

                                        <div className={s.pppRev}>
                                            <div className={s.pppRevT}>
                                                <span className={s.pppWho}>Anonymous</span>
                                                <span className={s.pppWhen}>A week ago</span>
                                            </div>
                                            <img className={s.pppStars} src={Stars4} alt="" />
                                            <p>
                                                &quot;Helpful visit overall. Would have liked a little more time to go
                                                through my options.&quot;
                                            </p>
                                            <div className={s.pppTags}>
                                                <span className={s.pppTag}>Knowledgeable</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className={s.pppBar}>
                                <div className={s.pppNav}>
                                    <img className={s.pppLogo} src={KlarityWordmark} alt="Klarity" />
                                    <img className={s.pppMenu} src={Menu} alt="" />
                                </div>
                            </div>
                            <div className={s.ppFade} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PatientPreview;
